import { serviceLogger } from './ServiceLogger'

export interface RetryOptions {
  maxAttempts?: number
  initialDelay?: number
  maxDelay?: number
  backoffMultiplier?: number
  shouldRetry?: (error: any, attempt: number) => boolean
  onRetry?: (error: any, attempt: number) => void
}

/**
 * Default retry options
 */
const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxAttempts: 3,
  initialDelay: 1000,
  maxDelay: 30000,
  backoffMultiplier: 2,
  shouldRetry: (error) => {
    // Retry on network errors and 5xx status codes
    if (error.code === 'NETWORK_ERROR') return true
    if (error.status >= 500 && error.status < 600) return true
    if (error.recoverable === true) return true
    return false
  },
  onRetry: (error, attempt) => {
    serviceLogger.info(`Retry attempt ${attempt}`, {
      error: error.message,
      code: error.code
    })
  }
}

/**
 * Exponential backoff retry strategy
 */
export class RetryStrategy {
  /**
   * Execute function with retry logic
   */
  static async execute<T>(
    fn: () => Promise<T>,
    options?: RetryOptions
  ): Promise<T> {
    const opts = { ...DEFAULT_OPTIONS, ...options }
    let lastError: any
    
    for (let attempt = 1; attempt <= opts.maxAttempts; attempt++) {
      try {
        return await fn()
      } catch (error) {
        lastError = error
        
        // Check if we should retry
        if (attempt === opts.maxAttempts || !opts.shouldRetry(error, attempt)) {
          throw error
        }
        
        // Calculate delay with exponential backoff
        const delay = Math.min(
          opts.initialDelay * Math.pow(opts.backoffMultiplier, attempt - 1),
          opts.maxDelay
        )
        
        // Add jitter to prevent thundering herd
        const jitter = Math.random() * 0.3 * delay
        const finalDelay = delay + jitter
        
        // Call retry callback
        opts.onRetry(error, attempt)
        
        // Wait before retrying
        await this.delay(finalDelay)
      }
    }
    
    throw lastError
  }
  
  /**
   * Execute with simple retry (no backoff)
   */
  static async simpleRetry<T>(
    fn: () => Promise<T>,
    maxAttempts: number = 3,
    delay: number = 1000
  ): Promise<T> {
    return this.execute(fn, {
      maxAttempts,
      initialDelay: delay,
      backoffMultiplier: 1 // No backoff
    })
  }
  
  /**
   * Execute with rate limit aware retry
   */
  static async retryWithRateLimit<T>(
    fn: () => Promise<T>,
    options?: RetryOptions
  ): Promise<T> {
    return this.execute(fn, {
      ...options,
      shouldRetry: (error, attempt) => {
        // Handle rate limit errors specially
        if (error.code === 'RATE_LIMIT_ERROR' || error.status === 429) {
          return attempt < (options?.maxAttempts || 3)
        }
        return options?.shouldRetry?.(error, attempt) ?? 
               DEFAULT_OPTIONS.shouldRetry(error, attempt)
      },
      onRetry: (error, attempt) => {
        if (error.retryAfter) {
          serviceLogger.info(`Rate limited, waiting ${error.retryAfter}ms`, {
            attempt,
            retryAfter: error.retryAfter
          })
        }
        options?.onRetry?.(error, attempt)
      }
    })
  }
  
  /**
   * Helper to delay execution
   */
  private static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
}

/**
 * Decorator for adding retry logic to methods
 */
export function withRetry(options?: RetryOptions) {
  return function (
    target: any,
    propertyKey: string,
    descriptor: PropertyDescriptor
  ) {
    const originalMethod = descriptor.value
    
    descriptor.value = async function (...args: any[]) {
      return RetryStrategy.execute(
        () => originalMethod.apply(this, args),
        options
      )
    }
    
    return descriptor
  }
}